import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function AccountSettings() {
  const { user, updateProfile, changePassword } = useAuth();

  // Profile form state
  const [profileData, setProfileData] = useState({
    fullName: user.fullName || '',
    tradingViewId: user.tradingViewId || '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState(null);

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
    setProfileSuccess(false);
    setProfileError(null);
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    setPasswordSuccess(false);
    setPasswordError(null);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(false);

    const result = await updateProfile(profileData);

    if (result.success) {
      setProfileSuccess(true);
    } else {
      setProfileError(result.error);
    }

    setProfileLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);

    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);

    if (result.success) {
      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } else {
      setPasswordError(result.error);
    }

    setPasswordLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Header */}
      <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <Link to="/account" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-2">Account Settings</h1>
          <p className="text-neutral-400">Update your profile information and password</p>
        </div>

        <div className="space-y-8">
          {/* Profile Information */}
          <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
            <h2 className="text-2xl font-black mb-6">Profile Information</h2>

            {profileSuccess && (
              <div className="bg-green-950/30 ring-1 ring-green-900/50 rounded-lg p-4 mb-6">
                <p className="text-green-400 text-sm">Profile updated successfully!</p>
              </div>
            )}

            {profileError && (
              <div className="bg-red-950/30 ring-1 ring-red-900/50 rounded-lg p-4 mb-6">
                <p className="text-red-400 text-sm">{profileError}</p>
              </div>
            )}

            <form onSubmit={handleProfileSubmit}>
              {/* Full Name */}
              <div className="mb-6">
                <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Your full name"
                  value={profileData.fullName}
                  onChange={handleProfileChange}
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>

              {/* Email (Read-only) */}
              <div className="mb-6">
                <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                  Email Address
                </label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full bg-neutral-900/50 ring-1 ring-neutral-800 rounded-lg px-4 py-3 text-neutral-500 cursor-not-allowed"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Email cannot be changed. Contact support if you need to update it.
                </p>
              </div>

              {/* TradingView ID */}
              <div className="mb-6">
                <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                  TradingView Username
                </label>
                <input
                  type="text"
                  name="tradingViewId"
                  placeholder="@yourusername"
                  value={profileData.tradingViewId}
                  onChange={handleProfileChange}
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  We'll use this to grant you access to indicators on TradingView
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={profileLoading}
                className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-3 rounded-lg font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>

          {/* Change Password */}
          <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
            <h2 className="text-2xl font-black mb-6">Change Password</h2>

            {passwordSuccess && (
              <div className="bg-green-950/30 ring-1 ring-green-900/50 rounded-lg p-4 mb-6">
                <p className="text-green-400 text-sm">Password changed successfully!</p>
              </div>
            )}

            {passwordError && (
              <div className="bg-red-950/30 ring-1 ring-red-900/50 rounded-lg p-4 mb-6">
                <p className="text-red-400 text-sm">{passwordError}</p>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit}>
              {/* Current Password */}
              <div className="mb-6">
                <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                  Current Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <input
                    type="password"
                    name="currentPassword"
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                  />
                </div>
              </div>

              {/* New Password */}
              <div className="mb-6">
                <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <input
                    type="password"
                    name="newPassword"
                    placeholder="At least 6 characters"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                  />
                </div>
              </div>

              {/* Confirm New Password */}
              <div className="mb-6">
                <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full bg-neutral-900 ring-1 ring-neutral-800 text-white py-3 rounded-lg font-bold hover:ring-[#CBB26A] hover:text-[#CBB26A] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Lock className="w-5 h-5" />
                {passwordLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
